import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "../lib/logger";
import { api, ApiError } from "../lib/api";
import { ControlOp, RECONNECT_TOKEN_KEY, type RemoteSource, type HostCaps } from "../lib/protocol";

const SIGNALING =
  import.meta.env.VITE_SIGNALING_URL ?? "ws://localhost:8081/ws";

// STUN is enough on a LAN; set VITE_TURN_URL/USERNAME/PASSWORD (baked at build)
// to add a TURN relay for NAT traversal across the internet.
const ICE_SERVERS: RTCIceServer[] = [
  { urls: import.meta.env.VITE_STUN_URL ?? "stun:stun.l.google.com:19302" },
  ...(import.meta.env.VITE_TURN_URL
    ? [
        {
          // Offer the relay over UDP, plain TCP, AND TLS/443. Mobile carriers /
          // locked-down networks block UDP (kills the default relay) and often
          // everything but 443; the extra candidates get media through:
          // ?transport=tcp for UDP-blocked nets, and turns:443 for 443-only,
          // SSL-inspecting corporate firewalls (looks exactly like HTTPS).
          urls: [
            import.meta.env.VITE_TURN_URL as string,
            `${import.meta.env.VITE_TURN_URL as string}?transport=tcp`,
            ...(import.meta.env.VITE_TURNS_URL
              ? [import.meta.env.VITE_TURNS_URL as string]
              : [])
          ],
          username: import.meta.env.VITE_TURN_USERNAME as string,
          credential: import.meta.env.VITE_TURN_PASSWORD as string
        }
      ]
    : [])
];

const FILE_CHUNK_SIZE = 8 * 1024; // base64-encoded ~11KB stays under safe data-channel msg size

// ---- Icons: one consistent 24x24 stroke line set, inherits currentColor ----
type IconName =
  | "upload" | "download" | "folder" | "file" | "clipboard" | "chat" | "send"
  | "cursor" | "eye" | "eyeOff" | "maximize" | "minimize" | "panel" | "check"
  | "x" | "arrowUp" | "swap" | "monitor" | "gauge" | "keyboard" | "record" | "stop"
  | "chevronDown" | "chevronUp" | "more" | "power" | "lock" | "shield";
const ICON_PATHS: Record<IconName, string> = {
  upload: "M12 19V6M6 12l6-6 6 6",
  download: "M12 5v13M18 12l-6 6-6-6",
  folder: "M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V7z",
  file: "M14 3v4a1 1 0 001 1h4M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z",
  clipboard: "M8 4h8v3H8zM8 4H6a2 2 0 00-2 2v13a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2",
  chat: "M21 11.5a8 8 0 01-11.5 7.2L3 21l2.3-5.5A8 8 0 1121 11.5z",
  send: "M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z",
  cursor: "M5 3l15 8-6.5 2L11 20 5 3z",
  eye: "M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7zM12 9.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z",
  eyeOff: "M3 3l18 18M10.6 10.7a2.5 2.5 0 003.5 3.5M9.9 5.2A10 10 0 0112 5c6.4 0 10 7 10 7a16 16 0 01-3 3.9M6 6.2A16 16 0 002 12s3.6 7 10 7a10 10 0 003-.5",
  maximize: "M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5",
  minimize: "M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5",
  panel: "M4 5h16v14H4zM14 5v14",
  check: "M20 6L9 17l-5-5",
  x: "M18 6L6 18M6 6l12 12",
  arrowUp: "M12 19V5M6 11l6-6 6 6",
  swap: "M4 8h13l-3-3M20 16H7l3 3",
  monitor: "M4 5h16v11H4zM8 20h8M12 16v4",
  gauge: "M12 13l4-4M5 18a9 9 0 1114 0z",
  keyboard: "M4 6h16v12H4zM7 10h.01M11 10h.01M15 10h.01M8 14h8",
  record: "M12 8a4 4 0 100 8 4 4 0 000-8z",
  stop: "M7 7h10v10H7z",
  chevronDown: "M6 9l6 6 6-6",
  chevronUp: "M6 15l6-6 6 6",
  more: "M5 12h.01M12 12h.01M19 12h.01",
  power: "M12 4v8M6.6 7.4a8 8 0 1010.8 0",
  lock: "M7 11V8a5 5 0 0110 0v3M5 11h14v9H5z",
  shield: "M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z"
};
function Icon({ n, c = "w-4 h-4" }: { n: IconName; c?: string }) {
  return (
    <svg
      className={c}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICON_PATHS[n]} />
    </svg>
  );
}

type Mods = {
  shift?: boolean;
  ctrl?: boolean;
  alt?: boolean;
  meta?: boolean;
};
type InputEvent =
  | { t: "mouse"; action: "move" | "down" | "up"; x: number; y: number; button?: number; mods?: Mods }
  | { t: "wheel"; dx: number; dy: number; mods?: Mods }
  | { t: "key"; action: "down" | "up"; key: string; code: string; mods?: Mods };

function modsOf(e: KeyboardEvent | MouseEvent | WheelEvent): Mods {
  const m: Mods = {};
  if (e.shiftKey) m.shift = true;
  if (e.ctrlKey) m.ctrl = true;
  if (e.altKey) m.alt = true;
  if (e.metaKey) m.meta = true;
  return m;
}

interface FileProgress {
  id: string;
  name: string;
  size: number;
  sent: number;
  status: "offered" | "transferring" | "done" | "rejected" | "error";
  detail?: string;
  dir: "up" | "down";
}

export function ViewerPage() {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const ticket = params.get("ticket");
  // v2 recording: device label/hostname threaded from ConnectByCode so a saved
  // recording gets a descriptive filename instead of an opaque session id.
  const deviceName = params.get("name");
  const videoRef = useRef<HTMLVideoElement>(null);

  const inputChannelRef = useRef<RTCDataChannel | null>(null);
  // Unreliable/unordered channel for pointer moves only (low-latency, no
  // head-of-line blocking). Falls back to inputChannelRef when absent.
  const moveChannelRef = useRef<RTCDataChannel | null>(null);
  const clipboardChannelRef = useRef<RTCDataChannel | null>(null);
  const fileChannelRef = useRef<RTCDataChannel | null>(null);
  const controlChannelRef = useRef<RTCDataChannel | null>(null);
  const chatChannelRef = useRef<RTCDataChannel | null>(null);
  // Mirrors `quality` for use inside stable channel callbacks, and gates the
  // host's one-time initial quality push so the viewer's default (Sharp text)
  // wins even against a host that hasn't been updated yet.
  const qualityRef = useRef<string>("sharp");
  const firstQualityPushRef = useRef<boolean>(true);
  const pendingFilesRef = useRef<Map<string, { file: File; resolveAccept: (ok: boolean) => void }>>(
    new Map()
  );
  // Assembles incoming remote-file downloads (id -> chunks + meta).
  const downloadsRef = useRef<Map<string, { name: string; size: number; chunks: Uint8Array[] }>>(
    new Map()
  );
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const startedRef = useRef(false);
  // v2 recording hardening: recNameRef holds the descriptive filename chosen at start,
  // recBytesRef/recStartRef drive the live "REC mm:ss · N MB" readout. A hard byte cap
  // (below) guards against a tab-crashing unbounded buffer on very long sessions.
  const recNameRef = useRef<string>("");
  const recBytesRef = useRef(0);
  const recStartRef = useRef(0);

  // v2 remote cursor overlay (view-only): the latest host pointer sample kept in a
  // REF (60Hz setState would thrash), the interpolated render position, the overlay
  // DOM node, and the last-seen seq for stale-drop on the unreliable channel.
  const cursorChannelRef = useRef<RTCDataChannel | null>(null);
  const cursorSampleRef = useRef<{ x: number; y: number; visible: boolean; shape: string; at: number } | null>(null);
  const cursorRenderRef = useRef<{ x: number; y: number; shown: boolean }>({ x: 0, y: 0, shown: false });
  const cursorOverlayRef = useRef<HTMLDivElement>(null);
  const cursorSeqRef = useRef(0);

  // v2 reboot + auto-reconnect: a server-minted reconnect token (device-bound), a flag
  // that the current drop is an intentional reboot (so only THEN do we auto re-dial),
  // and a cancel handle for the backoff poller.
  const reconnectTokenRef = useRef<string | null>(null);
  const rebootPendingRef = useRef(false);
  const reconnectCancelRef = useRef<(() => void) | null>(null);

  // Mirror refs so the always-on cursor rAF loop reads current values without re-binding.
  const hasControlRef = useRef(false);
  const displayModeRef = useRef<"fit" | "actual">("fit");
  const hasVideoRef = useRef(false);

  const [status, setStatus] = useState("connecting");
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | null>(null);
  const [channelReady, setChannelReady] = useState(false);
  const [hasControl, setHasControl] = useState(false);
  const [remoteClipboard, setRemoteClipboard] = useState<string>("");
  // Remote clipboard image (data: URL) — shown as a preview with a one-click copy.
  const [remoteImage, setRemoteImage] = useState<string>("");
  const [files, setFiles] = useState<FileProgress[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [stats, setStats] = useState<{ latency?: number; bandwidth?: number; fps?: number }>({});
  // Responsive toolbar: a "Tools" overflow menu (so nothing is cut off on
  // narrow / full-screen widths) and a hide/reveal toggle for max screen space.
  const [showTools, setShowTools] = useState(false);
  const [toolbarHidden, setToolbarHidden] = useState(false);
  const navigate = useNavigate();

  // Round 2 feature state
  const [sources, setSources] = useState<RemoteSource[]>([]);
  const [currentSource, setCurrentSource] = useState<string>("");
  const [quality, setQuality] = useState<string>("sharp");
  const [displayMode, setDisplayMode] = useState<"fit" | "actual">("fit");
  const [blankOn, setBlankOn] = useState(false);
  // Host-reported lock state + local "no frames arriving" detection: both mean
  // the user would otherwise stare at an unexplained black rectangle.
  const [hostLocked, setHostLocked] = useState(false);
  const [noVideo, setNoVideo] = useState(false);
  const zeroFpsTicksRef = useRef(0);
  const [recording, setRecording] = useState(false);
  // v2 recording live readout.
  const [recBytes, setRecBytes] = useState(0);
  const [recElapsed, setRecElapsed] = useState(0);
  // v2 host capabilities (control-channel "host-caps"): null until a v2 host announces
  // them. The Reboot button + monitor picker gate on this being non-null.
  const [hostCaps, setHostCaps] = useState<HostCaps | null>(null);
  // Secure desktop (UAC prompt / lock screen / Ctrl+Alt+Del screen) is up on the
  // remote, plus the newest still of it. Video is frozen for as long as this is
  // true, so these frames ARE the picture — and they cover the whole virtual
  // screen, which is why input maps to the image rect while it shows.
  const [secureDesktop, setSecureDesktop] = useState(false);
  const [secureFrame, setSecureFrame] = useState<string | null>(null);
  // The remote can't capture that screen (composited lock screens read back
  // black). Input still reaches it, so say that instead of showing black.
  const [secureBlank, setSecureBlank] = useState(false);
  const secureDesktopRef = useRef(false);
  const secureImgRef = useRef<HTMLImageElement | null>(null);
  // v2 reboot/auto-reconnect UI: whether we're re-dialing a rebooted host, the attempt
  // counter, whether the poller gave up, and the pre-flight strand-warning modal.
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [reconnectFailed, setReconnectFailed] = useState(false);
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const [showRebootModal, setShowRebootModal] = useState(false);
  // Reactive mirror of reconnectTokenRef so the reboot pre-flight can gate on it.
  const [hasReconnectToken, setHasReconnectToken] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{ from: "you" | "remote"; text: string }>>(
    []
  );
  const [chatInput, setChatInput] = useState("");
  const [browser, setBrowser] = useState<{
    path: string;
    parent: string;
    sep: string;
    entries: Array<{ name: string; isDir: boolean }>;
  } | null>(null);
  const [browseError, setBrowseError] = useState<string | null>(null);
  // Host capability broadcast (control-channel "perms"): null = unknown yet,
  // true/false = the host's current "allow input" / "allow file access" toggles.
  const [hostAllowsInput, setHostAllowsInput] = useState<boolean | null>(null);
  const [hostAllowsFiles, setHostAllowsFiles] = useState<boolean | null>(null);
  const [hasVideo, setHasVideo] = useState(false);
  // Per-channel open flags so controls can be disabled until their channel is up.
  const [controlReady, setControlReady] = useState(false);
  const [fileReady, setFileReady] = useState(false);
  const [chatReady, setChatReady] = useState(false);
  const [clipReady, setClipReady] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Ids of transfers the operator cancelled or the host aborted/denied — the
  // upload loop checks this to stop streaming immediately.
  const abortedRef = useRef<Set<string>>(new Set());

  // WebRTC + signaling lifecycle
  useEffect(() => {
    if (!sessionId || !ticket) {
      setStatus("missing session or ticket");
      return;
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const pendingIce: RTCIceCandidateInit[] = [];
    // Local ICE candidates gathered before the signaling socket is OPEN — queued
    // here and flushed on open so a socket-timing gap can't silently drop them
    // (relay/turns candidates gather last and were the ones most often lost).
    const localIce: RTCIceCandidateInit[] = [];
    let remoteDescSet = false;
    // Reconnecting signaling socket. The media peer can outlive a signaling blip,
    // so a dropped ws must reconnect + re-join — otherwise trickle-ICE and any
    // host-initiated ICE-restart re-offer can never reach us.
    let sock: WebSocket | null = null;
    let closing = false;
    let wsAttempts = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    // Credential for re-entering this session's signaling room after the socket
    // drops (the join ticket is single-use), plus the keepalive that stops the
    // socket from being closed as idle mid-session.
    let rejoinToken: string | null = null;
    let keepalive: ReturnType<typeof setInterval> | null = null;
    let canRejoin = true;

    pc.ontrack = (ev) => {
      const stream = ev.streams[0];
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
        setHasVideo(true);
      }
    };

    // Monitor connection stats
    const statsInterval = setInterval(async () => {
      try {
        const statsReport = await pc.getStats();
        let latency = 0;
        let bandwidth = 0;
        let fps = 0;
        statsReport.forEach((report) => {
          if (report.type === "inbound-rtp" && report.kind === "video") {
            bandwidth = Math.round((report.bytesReceived || 0) / 1024 / 1024 * 8) || 0;
            fps = Math.round(report.framesPerSecond || 0);
          }
          if (report.type === "candidate-pair" && report.state === "succeeded") {
            latency = Math.round((report.currentRoundTripTime || 0) * 1000);
          }
        });
        setStats({ latency, bandwidth, fps });
        // Silent-black detector: connected but no frames arriving for a while.
        // The host runs its own capture watchdog; this is the viewer-side hint
        // so the user knows what's happening (and what to do) meanwhile.
        if (fps > 0) {
          zeroFpsTicksRef.current = 0;
          setNoVideo(false);
        } else if (++zeroFpsTicksRef.current >= 6) {
          setNoVideo(true);
        }
      } catch {
        /* ignore stats errors */
      }
    }, 1000);

    pc.ondatachannel = (ev) => {
      const ch = ev.channel;
      if (ch.label === "input") {
        inputChannelRef.current = ch;
        ch.onopen = () => setChannelReady(true);
        ch.onclose = () => {
          setChannelReady(false);
          setHasControl(false);
        };
      } else if (ch.label === "input-move") {
        // Low-latency pointer-move channel. Control readiness stays gated on the
        // reliable "input" channel; moves just use this one when it's open.
        moveChannelRef.current = ch;
        ch.onclose = () => {
          moveChannelRef.current = null;
        };
      } else if (ch.label === "clipboard") {
        clipboardChannelRef.current = ch;
        ch.onopen = () => setClipReady(true);
        ch.onmessage = (e) => {
          try {
            const m = JSON.parse(e.data as string);
            if (m.op === "set" && typeof m.text === "string") {
              setRemoteClipboard(m.text);
            } else if (m.op === "set-image" && typeof m.data === "string") {
              setRemoteImage(m.data);
            }
          } catch (err) {
            logger.warn("clipboard channel: malformed message", { error: String(err) });
          }
        };
      } else if (ch.label === "file") {
        fileChannelRef.current = ch;
        ch.onopen = () => setFileReady(true);
        ch.onmessage = (e) => {
          try {
            const m = JSON.parse(e.data as string);
            if (m.op === "accept") {
              setHostAllowsFiles(true);
              const pending = pendingFilesRef.current.get(m.id);
              if (pending) pending.resolveAccept(true);
            } else if (m.op === "denied") {
              // The host has "Allow remote file access" turned off (the default).
              // Previously this message was dropped and uploads hung forever.
              setHostAllowsFiles(false);
              if (m.id) {
                abortedRef.current.add(m.id);
                const pending = pendingFilesRef.current.get(m.id);
                if (pending) pending.resolveAccept(false);
                setFiles((prev) =>
                  prev.map((f) =>
                    f.id === m.id
                      ? { ...f, status: "rejected", detail: "File access is off on the remote" }
                      : f
                  )
                );
              }
            } else if (m.op === "reject" || m.op === "abort") {
              if (m.id) abortedRef.current.add(m.id);
              const pending = pendingFilesRef.current.get(m.id);
              if (pending) pending.resolveAccept(false);
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === m.id
                    ? { ...f, status: "rejected", detail: m.reason ?? "rejected" }
                    : f
                )
              );
            } else if (m.op === "done") {
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === m.id ? { ...f, status: m.ok ? "done" : "error", detail: m.path } : f
                )
              );
            } else if (m.op === "listing") {
              setHostAllowsFiles(true);
              setBrowseError(null);
              setBrowser({ path: m.path, parent: m.parent, sep: m.sep, entries: m.entries });
            } else if (m.op === "listing-error") {
              setBrowseError(String(m.error ?? "Could not read that folder"));
            } else if (m.op === "dl-offer") {
              downloadsRef.current.set(m.id, { name: m.name, size: m.size, chunks: [] });
              setFiles((prev) => [
                ...prev,
                { id: m.id, name: m.name, size: m.size, sent: 0, status: "transferring", dir: "down" }
              ]);
            } else if (m.op === "dl-chunk") {
              const d = downloadsRef.current.get(m.id);
              if (d) {
                const bytes = base64ToBytes(m.data);
                d.chunks.push(bytes);
                const got = d.chunks.reduce((n, c) => n + c.length, 0);
                setFiles((prev) => prev.map((f) => (f.id === m.id ? { ...f, sent: got } : f)));
              }
            } else if (m.op === "dl-complete") {
              const d = downloadsRef.current.get(m.id);
              if (d) {
                saveBlob(new Blob(d.chunks as BlobPart[]), d.name);
                downloadsRef.current.delete(m.id);
                setFiles((prev) =>
                  prev.map((f) => (f.id === m.id ? { ...f, status: "done" } : f))
                );
              }
            } else if (m.op === "dl-error") {
              if (m.id) downloadsRef.current.delete(m.id);
              setFiles((prev) =>
                prev.map((f) => (f.id === m.id ? { ...f, status: "error", detail: m.error } : f))
              );
            }
            // op === "ack" — ignored; we drive progress from local sent bytes.
          } catch (err) {
            logger.warn("file channel: malformed message", { error: String(err) });
          }
        };
      } else if (ch.label === "control") {
        controlChannelRef.current = ch;
        ch.onopen = () => {
          setControlReady(true);
          // Command our default/selected quality so the host encodes it from the
          // first frame — this makes "Sharp text" the effective default even on a
          // host still defaulting to something else. The host's own one-time push
          // (handled below) is ignored so it can't flip us back.
          firstQualityPushRef.current = true;
          try {
            ch.send(JSON.stringify({ op: "quality", value: qualityRef.current }));
            // v2: tell the host we render the streamed remote cursor, so it may blank
            // its baked-in cursor even for us (view-only) -> one single smooth pointer.
            ch.send(JSON.stringify({ op: "cursor-overlay", supported: true }));
          } catch {
            /* channel not ready — host default still applies */
          }
        };
        ch.onmessage = (e) => {
          try {
            const m = JSON.parse(e.data as string);
            if (m.op === "sources") {
              setSources(m.sources ?? []);
              if (m.current) setCurrentSource(m.current);
            } else if (m.op === "quality") {
              // Swallow the host's initial quality announcement (we've already
              // commanded ours on connect); honor later host-side changes.
              if (firstQualityPushRef.current) {
                firstQualityPushRef.current = false;
              } else {
                setQuality(m.value);
                qualityRef.current = m.value;
              }
            } else if (m.op === "blank-state") {
              setBlankOn(!!m.on);
            } else if (m.op === "host-state") {
              // Host says its Windows session locked/unlocked — the lock screen
              // is a secure desktop that cannot be captured, so explain the
              // black picture instead of leaving it a mystery.
              setHostLocked(!!m.locked);
            } else if (m.op === "perms") {
              // Host told us which capabilities it currently allows.
              setHostAllowsInput(!!m.input);
              setHostAllowsFiles(!!m.files);
            } else if (m.op === "secure-desktop") {
              // A UAC prompt / lock screen / Ctrl+Alt+Del screen took over the
              // remote's input. The video is frozen until it goes away; switch to
              // the stills the host is pushing on the "secure" channel.
              setSecureDesktop(!!m.on);
              setSecureBlank(!!m.blank);
              if (!m.on) {
                setSecureFrame((prev) => {
                  if (prev) URL.revokeObjectURL(prev);
                  return null;
                });
              }
            } else if (m.op === "host-caps") {
              // v2 host capability snapshot — gates the Reboot button + monitor picker.
              setHostCaps(m as HostCaps);
            } else if (m.op === "rebooting") {
              // The host is about to reboot on our request (or another operator's). Only a
              // viewer that actually holds a reconnect token can auto re-dial — a view-only
              // or additional viewer has none, so it must fall through to the ordinary
              // disconnect card rather than a dead-end "couldn't reconnect" state.
              if (reconnectTokenRef.current) {
                rebootPendingRef.current = true;
                setReconnecting(true);
                setReconnectFailed(false);
              }
            }
          } catch (err) {
            logger.warn("control channel: malformed message", { error: String(err) });
          }
        };
      } else if (ch.label === "chat") {
        chatChannelRef.current = ch;
        ch.onopen = () => setChatReady(true);
        ch.onmessage = (e) => {
          try {
            const m = JSON.parse(e.data as string);
            if (m.op === "msg" && typeof m.text === "string") {
              setChatMessages((prev) => [...prev, { from: "remote", text: m.text }]);
            }
          } catch (err) {
            logger.warn("chat channel: malformed message", { error: String(err) });
          }
        };
      } else if (ch.label === "secure") {
        // JPEG stills of the SECURE DESKTOP — the separate, protected desktop
        // Windows puts the UAC consent dialog, the lock screen and the
        // Ctrl+Alt+Del screen on. Screen capture cannot see it (the video track
        // just freezes), so the host's elevated service captures it and sends it
        // here. Without this the operator is clicking a prompt they can't see.
        ch.binaryType = "arraybuffer";
        // Frames arrive in chunks ([id lo][id hi][index][count] + payload)
        // because a whole-screen JPEG can exceed the data channel's maximum
        // message size. Collect one frame at a time and paint it when complete.
        let pendingId = -1;
        let pendingChunks: Uint8Array[] = [];
        let pendingSeen = 0;
        ch.onmessage = (e) => {
          try {
            const buf = new Uint8Array(e.data as ArrayBuffer);
            if (buf.length < 4) return;
            const id = (buf[0] ?? 0) | ((buf[1] ?? 0) << 8);
            const index = buf[2] ?? 0;
            const count = buf[3] ?? 0;
            if (count === 0 || index >= count) return;
            if (id !== pendingId) {
              // New frame: abandon any incomplete one rather than mixing them.
              pendingId = id;
              pendingChunks = new Array(count);
              pendingSeen = 0;
            }
            if (pendingChunks[index]) return; // duplicate
            pendingChunks[index] = buf.subarray(4);
            pendingSeen++;
            if (pendingSeen !== count) return;
            const blob = new Blob(pendingChunks as BlobPart[], { type: "image/jpeg" });
            pendingId = -1;
            pendingChunks = [];
            pendingSeen = 0;
            const url = URL.createObjectURL(blob);
            setSecureFrame((prev) => {
              // Exactly one object URL alive at a time; the previous frame is
              // already painted and leaking them would grow without bound.
              if (prev) URL.revokeObjectURL(prev);
              return url;
            });
          } catch {
            /* ignore malformed frame */
          }
        };
      } else if (ch.label === "cursor") {
        // v2 remote cursor (agent->viewer, unreliable). Store the newest sample in a
        // ref (no setState — arrives ~60Hz); the rAF loop below interpolates + draws it.
        // The channel is unordered, so drop any sample whose seq isn't newer (uint32
        // modular window handles wraparound).
        cursorChannelRef.current = ch;
        // Reset the seq baseline for this (possibly rebooted) host — its module-global
        // cursorSeq restarts at 0, and this ViewerPage instance persists across an
        // auto-reconnect navigate, so a retained high last-seq would reject the new stream.
        cursorSeqRef.current = 0;
        ch.onmessage = (e) => {
          try {
            const m = JSON.parse(e.data as string);
            if (m.t !== "cursor") return;
            // Drop stale/out-of-order samples (unordered channel). Coerce the uint32
            // difference with >>> 0 — a signed `& 0xffffffff` maxes at 0x7fffffff and
            // could never reach the 0x80000000 "not-newer" window (the guard was dead).
            if (((m.seq - cursorSeqRef.current) >>> 0) >= 0x80000000) return;
            cursorSeqRef.current = m.seq;
            cursorSampleRef.current = {
              x: m.x,
              y: m.y,
              visible: !!m.visible,
              shape: m.shape || "default",
              at: performance.now()
            };
          } catch {
            /* ignore malformed sample */
          }
        };
        ch.onclose = () => {
          cursorChannelRef.current = null;
        };
      }
    };

    pc.onconnectionstatechange = () => {
      setConnectionState(pc.connectionState as RTCPeerConnectionState);
      setStatus(pc.connectionState);
      // Mark the session active in history the first time media connects.
      if (pc.connectionState === "connected" && !startedRef.current) {
        startedRef.current = true;
        api(`/v1/sessions/${sessionId}/start`, { method: "POST" }).catch(() => {});
      }
      // v2: a fresh connection means any pending reboot reconnect has succeeded.
      if (pc.connectionState === "connected") {
        rebootPendingRef.current = false;
        setReconnecting(false);
        setReconnectAttempt(0);
        setReconnectFailed(false);
      }
    };

    pc.onicecandidate = (ev) => {
      if (!ev.candidate) return;
      const cand = ev.candidate.toJSON();
      if (sock && sock.readyState === sock.OPEN) {
        sock.send(JSON.stringify({ type: "ice", sessionId, payload: cand }));
      } else {
        localIce.push(cand);
      }
    };

    const connectWs = () => {
      const s = new WebSocket(SIGNALING);
      sock = s;
      s.onopen = () => {
        wsAttempts = 0;
        setStatus("joining");
        // After the first join the ticket is spent, so a reconnect uses the
        // re-join credential the server handed us instead. Without it a dropped
        // signaling socket could never get back into the room.
        s.send(
          rejoinToken
            ? JSON.stringify({ type: "join", sessionId, rejoin: rejoinToken })
            : JSON.stringify({ type: "join", sessionId, ticket })
        );
        // Flush candidates gathered before the socket was ready / during a blip.
        while (localIce.length > 0) {
          s.send(JSON.stringify({ type: "ice", sessionId, payload: localIce.shift() }));
        }
        // Keepalive. This socket is otherwise SILENT once ICE settles, and a
        // silent socket looks idle: the server used to close it after ten
        // minutes, which told the host "peer-left" and killed a session whose
        // media was perfectly healthy. Also keeps proxies from idling us out.
        if (keepalive) clearInterval(keepalive);
        keepalive = setInterval(() => {
          if (s.readyState === WebSocket.OPEN) {
            try {
              s.send(JSON.stringify({ type: "heartbeat", sessionId }));
            } catch {
              /* the close handler will reconnect */
            }
          }
        }, 25_000);
      };

      s.onmessage = async (ev) => {
        const msg = JSON.parse(ev.data as string);
        try {
          if (msg.type === "joined") {
            if (typeof msg.rejoin === "string") rejoinToken = msg.rejoin;
            setStatus("waiting for host");
          } else if (msg.type === "offer") {
            // Handles the initial offer AND a host ICE-restart re-offer
            // (renegotiation): re-set the remote description and answer either way.
            setStatus("received offer");
            await pc.setRemoteDescription(msg.payload);
            remoteDescSet = true;
            for (const c of pendingIce) await pc.addIceCandidate(c);
            pendingIce.length = 0;
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            s.send(JSON.stringify({ type: "answer", sessionId, payload: answer }));
          } else if (msg.type === "ice") {
            if (remoteDescSet) await pc.addIceCandidate(msg.payload);
            else pendingIce.push(msg.payload);
          } else if (msg.type === "peer-left") {
            // Only the host's SIGNALING socket went away. If media is still
            // flowing the session is fine and it will re-join in a moment —
            // don't tell the operator they were disconnected when they weren't.
            setStatus(
              pc.connectionState === "connected" ? "host signaling reconnecting…" : "host disconnected"
            );
          } else if (msg.type === "terminated") {
            // An admin force-ended this connection.
            setStatus("session ended by admin");
            closing = true;
            try {
              pc.close();
            } catch {
              /* ignore */
            }
            try {
              s.close();
            } catch {
              /* ignore */
            }
          } else if (msg.type === "error") {
            // A rejected credential can't be retried into working — stop the
            // reconnect loop from hammering a room we can no longer enter.
            if (msg.error === "invalid_rejoin" || msg.error === "invalid_ticket") canRejoin = false;
            setStatus(`error: ${msg.error}`);
          }
        } catch (err) {
          console.error("viewer message handler", err);
        }
      };

      s.onerror = () => setStatus("signaling error");
      s.onclose = () => {
        if (keepalive) {
          clearInterval(keepalive);
          keepalive = null;
        }
        if (closing) return;
        // Signaling dropped, but the media peer is very likely still alive — the
        // two are independent. Keep re-joining for as long as the session lasts:
        // giving up used to strand a working session with no way to trickle ICE
        // or accept an ICE-restart offer. Back off to 5s and keep trying quietly;
        // the picture keeps running the whole time.
        wsAttempts++;
        const alive = pc.connectionState === "connected" || pc.connectionState === "connecting";
        // Nothing left to restore: the media is gone too, or the server told us
        // our credential is no good. Stop after a bounded number of tries rather
        // than reconnecting to an empty room forever.
        if ((!alive || !canRejoin) && wsAttempts > 10) {
          setStatus("disconnected");
          return;
        }
        setStatus(alive ? "restoring signaling…" : "reconnecting…");
        reconnectTimer = setTimeout(connectWs, Math.min(1000 * wsAttempts, 5000));
      };
    };

    connectWs();

    return () => {
      closing = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (keepalive) clearInterval(keepalive);
      clearInterval(statsInterval);
      try {
        sock?.close();
      } catch {
        /* ignore */
      }
      // v2: flush an in-progress recording so a mid-session disconnect/unmount saves a
      // partial instead of losing it (mr.onstop writes + finalizes).
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      pc.close();
      inputChannelRef.current = null;
      moveChannelRef.current = null;
      clipboardChannelRef.current = null;
      fileChannelRef.current = null;
      cursorChannelRef.current = null;
      // Mark the session ended in history (best-effort).
      if (startedRef.current) {
        api(`/v1/sessions/${sessionId}/end`, { method: "POST", body: "{}" }).catch(() => {});
      }
    };
  }, [sessionId, ticket]);

  // Terminal disconnect (the remote went away — e.g. it restarted). WebRTC can't
  // recover a "failed"/"closed" peer, and the <video> would otherwise keep showing
  // the last decoded frame (a frozen desktop). Clear it so the viewer shows a clean
  // "disconnected" screen instead of a stale image, and drop the misleading
  // "retrying capture…" hint (which only applies while still connected).
  const connectionLost = connectionState === "failed" || connectionState === "closed";
  useEffect(() => {
    if (!connectionLost) return;
    if (videoRef.current) videoRef.current.srcObject = null;
    setHasVideo(false);
    setNoVideo(false);
    // v2: save a partial recording if one was running when the peer dropped.
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    // v2: if the drop was a reboot we triggered AND we hold a device-bound reconnect
    // token, enter reconnect mode — the backoff poller effect below re-dials by-token.
    // Any other drop (crash, network, admin-terminate) shows the manual card.
    if (rebootPendingRef.current && reconnectTokenRef.current) {
      setReconnecting(true);
    }
  }, [connectionLost]);

  // Input capture
  useEffect(() => {
    if (!hasControl) return;
    const video = videoRef.current;
    if (!video) return;

    function send(msg: InputEvent) {
      const ch = inputChannelRef.current;
      if (ch && ch.readyState === "open") ch.send(JSON.stringify(msg));
    }
    // Pointer moves go over the unreliable/unordered channel so a lost packet
    // never delays the moves after it — that head-of-line blocking on a reliable
    // channel is what made the cursor feel laggy/jumpy on relayed links. Falls
    // back to the reliable channel if the host is an older build without it.
    function sendMove(msg: InputEvent) {
      const mch = moveChannelRef.current;
      if (mch && mch.readyState === "open") {
        mch.send(JSON.stringify(msg));
        return;
      }
      send(msg);
    }
    // Map a viewport point to a 0..1 position on the remote screen, using a
    // pre-measured rect. In "fit" mode the <video> uses object-contain: the
    // picture is scaled to fit and centered, so it occupies only PART of the
    // element (letterbox bars fill the rest). Map to the actual image rect — not
    // the element rect — or the remote cursor drifts / scales wrong near the edges.
    function computeRel(clientX: number, clientY: number, rect: DOMRect) {
      // While the secure desktop is up, the picture the operator is aiming at is
      // the still we are painting over the frozen video — and it covers the whole
      // virtual screen, not the captured monitor. Map against THAT or every click
      // on a UAC prompt lands somewhere else entirely.
      const simg = secureDesktopRef.current ? secureImgRef.current : null;
      if (simg && simg.naturalWidth > 0) {
        const r = simg.getBoundingClientRect();
        const scale = Math.min(r.width / simg.naturalWidth, r.height / simg.naturalHeight);
        const dispW = simg.naturalWidth * scale;
        const dispH = simg.naturalHeight * scale;
        const offX = (r.width - dispW) / 2;
        const offY = (r.height - dispH) / 2;
        return {
          x: Math.max(0, Math.min(1, (clientX - r.left - offX) / dispW)),
          y: Math.max(0, Math.min(1, (clientY - r.top - offY) / dispH))
        };
      }
      const vw = video!.videoWidth;
      const vh = video!.videoHeight;
      let relX: number;
      let relY: number;
      if (vw > 0 && vh > 0 && displayMode === "fit") {
        const scale = Math.min(rect.width / vw, rect.height / vh);
        const dispW = vw * scale;
        const dispH = vh * scale;
        const offX = (rect.width - dispW) / 2;
        const offY = (rect.height - dispH) / 2;
        relX = (clientX - rect.left - offX) / dispW;
        relY = (clientY - rect.top - offY) / dispH;
      } else {
        relX = (clientX - rect.left) / rect.width;
        relY = (clientY - rect.top) / rect.height;
      }
      return { x: Math.max(0, Math.min(1, relX)), y: Math.max(0, Math.min(1, relY)) };
    }
    // Coalesce mouse-moves to one per animation frame (~60/sec). Crucially, store
    // only the RAW pointer coords per event and measure the element rect just
    // once per frame in flush — calling getBoundingClientRect() on every raw
    // mousemove (100s/sec) forces a synchronous layout each time and is itself a
    // major source of input lag.
    // What the REMOTE currently believes is held down, so we can lift exactly
    // that when focus leaves (see releaseHeld below).
    const keysDown = new Set<string>();
    const buttonsDown = new Set<number>();
    const lastPos = { x: 0.5, y: 0.5 };

    let pendingMove: { clientX: number; clientY: number; mods: Mods } | null = null;
    let moveRaf = 0;
    function flushMove() {
      moveRaf = 0;
      if (pendingMove) {
        const { x, y } = computeRel(pendingMove.clientX, pendingMove.clientY, video!.getBoundingClientRect());
        lastPos.x = x;
        lastPos.y = y;
        sendMove({ t: "mouse", action: "move", x, y, mods: pendingMove.mods });
        pendingMove = null;
      }
    }
    function onMouseMove(e: MouseEvent) {
      pendingMove = { clientX: e.clientX, clientY: e.clientY, mods: modsOf(e) };
      if (!moveRaf) moveRaf = requestAnimationFrame(flushMove);
    }
    // A viewer text field (chat / clipboard) is "focused" iff the active element is
    // editable — while one is, keystrokes must type there locally, not forward.
    function editableFocused() {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return el.isContentEditable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    }
    function onMouseDown(e: MouseEvent) {
      e.preventDefault();
      // Clicking the remote screen hands the keyboard back to it: if a viewer text
      // field was focused, blur it so subsequent keystrokes forward to the remote.
      if (editableFocused()) (document.activeElement as HTMLElement).blur();
      const { x, y } = computeRel(e.clientX, e.clientY, video!.getBoundingClientRect());
      send({ t: "mouse", action: "down", x, y, button: e.button, mods: modsOf(e) });
      buttonsDown.add(e.button);
    }
    function onMouseUp(e: MouseEvent) {
      e.preventDefault();
      const { x, y } = computeRel(e.clientX, e.clientY, video!.getBoundingClientRect());
      send({ t: "mouse", action: "up", x, y, button: e.button, mods: modsOf(e) });
      buttonsDown.delete(e.button);
    }
    // A drag that ends outside the video (over the toolbar, the sidebar, or off
    // the window entirely) never fires mouseup ON the video — the remote button
    // would stay pressed and the host would look frozen mid-drag. Catch it at the
    // window instead, but only for buttons we actually pressed on the picture.
    function onWindowMouseUp(e: MouseEvent) {
      if (!buttonsDown.has(e.button)) return;
      buttonsDown.delete(e.button);
      const { x, y } = computeRel(e.clientX, e.clientY, video!.getBoundingClientRect());
      send({ t: "mouse", action: "up", x, y, button: e.button, mods: modsOf(e) });
    }
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      send({ t: "wheel", dx: e.deltaX, dy: e.deltaY, mods: modsOf(e) });
    }
    function onContextMenu(e: Event) {
      e.preventDefault();
    }
    function onKeyDown(e: KeyboardEvent) {
      // Let the operator type in the viewer's own chat / clipboard fields; every
      // other key (including Escape) forwards to the remote, RDP-style.
      if (editableFocused()) return;
      e.preventDefault();
      keysDown.add(e.code);
      send({ t: "key", action: "down", key: e.key, code: e.code, mods: modsOf(e) });
    }
    function onKeyUp(e: KeyboardEvent) {
      if (editableFocused()) return;
      e.preventDefault();
      keysDown.delete(e.code);
      send({ t: "key", action: "up", key: e.key, code: e.code, mods: modsOf(e) });
    }

    /**
     * Lift everything we are holding on the remote.
     *
     * This is the fix for the "remote keyboard randomly stops typing" report.
     * Any key-down we forwarded whose key-up goes somewhere else leaves that key
     * latched on the HOST: press Alt+Tab to switch away from the viewer and the
     * host keeps Alt held down, so every later keystroke arrives there as
     * Alt+<key> — menus open, nothing types, and it looks like the keyboard died.
     * The browser gives the key-up to whatever took focus, never to us, so the
     * only cure is to release on the way out.
     */
    function releaseHeld() {
      for (const code of Array.from(keysDown)) {
        send({ t: "key", action: "up", key: "", code });
      }
      keysDown.clear();
      for (const b of Array.from(buttonsDown)) {
        send({ t: "mouse", action: "up", x: lastPos.x, y: lastPos.y, button: b });
      }
      buttonsDown.clear();
      // Belt and braces: tell the host to drop anything it still thinks is held,
      // in case a key-up above was lost with the channel closing.
      const ctl = controlChannelRef.current;
      if (ctl && ctl.readyState === "open") {
        try {
          ctl.send(JSON.stringify({ op: ControlOp.RELEASE_KEYS }));
        } catch {
          /* ignore */
        }
      }
    }
    function onBlur() {
      releaseHeld();
    }
    function onVisibility() {
      if (document.hidden) releaseHeld();
    }

    video.addEventListener("mousemove", onMouseMove);
    video.addEventListener("mousedown", onMouseDown);
    video.addEventListener("mouseup", onMouseUp);
    video.addEventListener("wheel", onWheel, { passive: false });
    video.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("mouseup", onWindowMouseUp);
    window.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onBlur);
    return () => {
      if (moveRaf) cancelAnimationFrame(moveRaf);
      // Control is ending (host revoked input, we're unmounting, mode changed):
      // never leave the remote holding keys we will no longer release.
      releaseHeld();
      video.removeEventListener("mousemove", onMouseMove);
      video.removeEventListener("mousedown", onMouseDown);
      video.removeEventListener("mouseup", onMouseUp);
      video.removeEventListener("wheel", onWheel);
      video.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("mouseup", onWindowMouseUp);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onBlur);
    };
  }, [hasControl, displayMode]);

  // Fullscreen: actually drive the browser Fullscreen API from the toggle, and
  // keep state in sync when the user leaves fullscreen with Esc.
  useEffect(() => {
    if (isFullscreen && !document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else if (!isFullscreen && document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
  }, [isFullscreen]);

  useEffect(() => {
    const onFsChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      // Keyboard Lock (Chromium, fullscreen only): routes keys the browser
      // normally eats — Escape, Alt+Tab, the Windows key — to the page instead,
      // so they reach the remote machine like they would in RDP. Ctrl+Alt+Del is
      // never capturable and doesn't need to be: the toolbar button raises the
      // real one through the host's elevated service.
      const kb = (navigator as unknown as { keyboard?: { lock?: (k?: string[]) => Promise<void>; unlock?: () => void } })
        .keyboard;
      try {
        if (fs) void kb?.lock?.();
        else kb?.unlock?.();
      } catch {
        /* not supported here — plain key forwarding still works */
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Mirror for the input handlers, which read it outside React's render cycle.
  useEffect(() => {
    secureDesktopRef.current = secureDesktop;
  }, [secureDesktop]);

  // Don't leak the last object URL when the session ends.
  useEffect(() => {
    return () => {
      setSecureFrame((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  // RDP / TeamViewer model: control is automatic. The operator opened this session
  // to drive the machine, so as soon as control is possible we grab it — there is no
  // manual "Take Control" step. If the host hasn't enabled (or later revokes) "Allow
  // remote mouse & keyboard", control drops on its own; when it's (re-)allowed, it
  // resumes. Mirroring the readiness state keeps the "Control: Active" readout honest.
  useEffect(() => {
    setHasControl(channelReady && hostAllowsInput !== false);
  }, [channelReady, hostAllowsInput]);

  // Keep mirror refs current so the always-on cursor rAF loop reads live values
  // (hasControl / displayMode / hasVideo) without re-subscribing every change.
  useEffect(() => {
    hasControlRef.current = hasControl;
  }, [hasControl]);
  useEffect(() => {
    displayModeRef.current = displayMode;
  }, [displayMode]);
  useEffect(() => {
    hasVideoRef.current = hasVideo;
  }, [hasVideo]);

  // v2 remote-cursor overlay: an always-on rAF loop that interpolates toward the latest
  // host sample and positions a pointer-events-none glyph on the video image rect. Shown
  // only for VIEW-ONLY viewers (while controlling, the native crosshair is the pointer).
  // Uses the exact inverse of computeRel so it lines up in both fit + actual modes.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const ov = cursorOverlayRef.current;
      const video = videoRef.current;
      const container = video?.parentElement;
      if (!ov || !video || !container) return;
      const s = cursorSampleRef.current;
      const now = performance.now();
      const stale = !s || now - s.at > 600;
      const show = !!s && s.visible && !stale && !hasControlRef.current && hasVideoRef.current;
      const r = cursorRenderRef.current;
      if (!show) {
        if (r.shown) {
          ov.style.display = "none";
          r.shown = false;
        }
        return;
      }
      if (!r.shown) {
        r.x = s!.x;
        r.y = s!.y;
        r.shown = true;
        ov.style.display = "block";
      } else {
        // ~3-frame smoothing bridges dropped packets without visible lag.
        r.x += (s!.x - r.x) * 0.4;
        r.y += (s!.y - r.y) * 0.4;
      }
      const vr = video.getBoundingClientRect();
      const cr = container.getBoundingClientRect();
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      let px: number;
      let py: number;
      if (vw > 0 && vh > 0 && displayModeRef.current === "fit") {
        const scale = Math.min(vr.width / vw, vr.height / vh);
        const dispW = vw * scale;
        const dispH = vh * scale;
        const offX = (vr.width - dispW) / 2;
        const offY = (vr.height - dispH) / 2;
        px = vr.left + offX + r.x * dispW;
        py = vr.top + offY + r.y * dispH;
      } else {
        px = vr.left + r.x * vr.width;
        py = vr.top + r.y * vr.height;
      }
      const left = px - cr.left;
      const top = py - cr.top;
      if (ov.dataset.shape !== s!.shape) {
        ov.dataset.shape = s!.shape;
        ov.innerHTML = cursorGlyph(s!.shape);
      }
      const [hx, hy] = CURSOR_HOTSPOT[s!.shape] ?? [0, 0];
      ov.style.transform = `translate(${left - hx}px, ${top - hy}px)`;
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Clipboard send (browser -> remote). Prefer an image on the clipboard, fall
  // back to text — so copying an image locally and clicking Send pastes it there.
  const sendClipboard = useCallback(async () => {
    const ch = clipboardChannelRef.current;
    if (!ch || ch.readyState !== "open") return;
    try {
      if (navigator.clipboard.read) {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const type = item.types.find((t) => t.startsWith("image/"));
          if (type) {
            const blob = await item.getType(type);
            const dataUrl = await blobToDataUrl(blob);
            ch.send(JSON.stringify({ op: "set-image", data: dataUrl }));
            return;
          }
        }
      }
    } catch {
      /* fall through to text */
    }
    try {
      const text = await navigator.clipboard.readText();
      ch.send(JSON.stringify({ op: "set", text: text.slice(0, 8192) }));
    } catch (err) {
      console.warn("clipboard read failed", err);
    }
  }, []);

  const copyRemoteClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(remoteClipboard);
    } catch (err) {
      console.warn("clipboard write failed", err);
    }
  }, [remoteClipboard]);

  // Put the remote clipboard image onto the local clipboard (a user gesture, so
  // the async Clipboard API's image-write permission is satisfied).
  const copyRemoteImage = useCallback(async () => {
    if (!remoteImage) return;
    try {
      const blob = await (await fetch(remoteImage)).blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type || "image/png"]: blob })]);
    } catch (err) {
      console.warn("clipboard image write failed", err);
    }
  }, [remoteImage]);

  // File upload
  const uploadFile = useCallback(async (file: File) => {
    const ch = fileChannelRef.current;
    if (!ch || ch.readyState !== "open") return;
    const id = crypto.randomUUID();
    const entry: FileProgress = {
      id,
      name: file.name,
      size: file.size,
      sent: 0,
      status: "offered",
      dir: "up"
    };
    setFiles((prev) => [...prev, entry]);

    const accepted = await new Promise<boolean>((resolve) => {
      pendingFilesRef.current.set(id, { file, resolveAccept: resolve });
      ch.send(
        JSON.stringify({
          op: "offer",
          id,
          name: file.name,
          size: file.size,
          mime: file.type || "application/octet-stream"
        })
      );
    });
    pendingFilesRef.current.delete(id);
    if (!accepted) return;

    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: "transferring" } : f)));

    let seq = 0;
    let offset = 0;
    while (offset < file.size) {
      // Stop immediately if the host aborted/denied or the operator cancelled.
      if (abortedRef.current.has(id)) return;
      // Throttle if the channel is buffering too much (1 MB high-water).
      while (ch.bufferedAmount > 1_000_000) {
        if (abortedRef.current.has(id)) return;
        await new Promise((r) => setTimeout(r, 20));
      }
      const slice = file.slice(offset, Math.min(offset + FILE_CHUNK_SIZE, file.size));
      const buf = await slice.arrayBuffer();
      const b64 = arrayBufferToBase64(buf);
      ch.send(JSON.stringify({ op: "chunk", id, seq, data: b64 }));
      seq++;
      offset += slice.size;
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, sent: offset } : f))
      );
    }
    if (abortedRef.current.has(id)) return;
    ch.send(JSON.stringify({ op: "complete", id }));
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const list = Array.from(e.dataTransfer.files);
      for (const f of list) uploadFile(f);
    },
    [uploadFile]
  );

  // ---- Control channel commands (viewer -> agent) ----
  const ctlSend = useCallback((obj: unknown) => {
    const ch = controlChannelRef.current;
    if (ch && ch.readyState === "open") ch.send(JSON.stringify(obj));
  }, []);
  const changeQuality = useCallback(
    (v: string) => {
      setQuality(v);
      qualityRef.current = v;
      ctlSend({ op: "quality", value: v });
    },
    [ctlSend]
  );
  const changeSource = useCallback(
    (id: string) => {
      setCurrentSource(id);
      ctlSend({ op: "switch-source", id });
    },
    [ctlSend]
  );
  // v2 monitor picker: "monitors" = the real screens plus the all-monitors composite.
  // cycleMonitor steps prev/next through them (falls back to the whole list for an old
  // host that only sent a flat, un-kinded list).
  const monitorSources = sources.filter((s) => s.kind === "screen" || s.kind === "all");
  const cycleMonitor = useCallback(
    (dir: 1 | -1) => {
      const list = monitorSources.length ? monitorSources : sources;
      if (!list.length) return;
      const i = list.findIndex((s) => s.id === currentSource);
      const next = list[(i + dir + list.length) % list.length];
      if (next) changeSource(next.id);
    },
    [monitorSources, sources, currentSource, changeSource]
  );
  const sendKeycombo = useCallback((combo: string) => ctlSend({ op: "keycombo", combo }), [ctlSend]);
  const toggleBlank = useCallback(() => {
    const next = !blankOn;
    setBlankOn(next);
    ctlSend({ op: "blank", on: next });
  }, [ctlSend, blankOn]);

  // ---- v2 remote reboot + auto-reconnect ----
  // Mint a device-bound reconnect token ONCE we hold a control session on a v2 host, so
  // a viewer-triggered reboot can re-dial by-token — no code/password stored in the browser.
  // Depend on a stable boolean (not the hostCaps object, which gets a new reference on
  // every host-caps message) and bail if a token already exists, so we mint once, not on
  // every perm toggle / re-broadcast (which would burn the 30/min rate limit).
  const isV2Host = hostCaps !== null;
  useEffect(() => {
    if (!sessionId || !hasControl || !isV2Host || reconnectTokenRef.current) return;
    let cancelled = false;
    api<{ reconnectToken: string; expiresIn: number }>(`/v1/connect/reconnect-token`, {
      method: "POST",
      body: JSON.stringify({ sessionId })
    })
      .then((r) => {
        if (cancelled) return;
        reconnectTokenRef.current = r.reconnectToken;
        try {
          sessionStorage.setItem(RECONNECT_TOKEN_KEY, r.reconnectToken);
        } catch {
          /* private mode — token stays in the ref for this tab */
        }
        setHasReconnectToken(true);
      })
      .catch(() => {
        /* token unavailable -> reboot still possible, reconnect falls back to manual */
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, hasControl, isV2Host]);

  // Backoff by-token re-dial. Runs only while `reconnecting` (armed by op:'rebooting' or
  // the connectionLost handler). Tolerates 409 device_offline while the host reboots;
  // stops on 401/403 (token dead), on success (navigate to the fresh session), the ~3min
  // deadline, or Cancel.
  useEffect(() => {
    // Show the overlay as soon as `reconnecting` is armed, but only START polling
    // /by-token once the peer has ACTUALLY dropped. The host stays online for ~3s
    // after we send op:'reboot' (shutdown /r /t 3) and its online key has a 90s TTL, so
    // polling early would create a session to the still-alive pre-reboot host, rotate
    // the token, disarm rebootPendingRef, and break the real reconnect. Wait for
    // connectionLost (failed/closed) so the first attempt lands after it's genuinely down.
    if (!reconnecting || !connectionLost) return;
    if (!reconnectTokenRef.current) {
      setReconnectFailed(true);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    const deadline = performance.now() + 3 * 60 * 1000;
    const tick = async () => {
      if (cancelled) return;
      attempt++;
      setReconnectAttempt(attempt);
      try {
        const r = await api<{ sessionId: string; viewerTicket: string; reconnectToken: string }>(
          `/v1/connect/by-token`,
          {
            method: "POST",
            body: JSON.stringify({ reconnectToken: reconnectTokenRef.current, mode: "control" })
          }
        );
        if (cancelled) return;
        reconnectTokenRef.current = r.reconnectToken; // server rotates it each success
        try {
          sessionStorage.setItem(RECONNECT_TOKEN_KEY, r.reconnectToken);
        } catch {
          /* ignore */
        }
        rebootPendingRef.current = false;
        const nm = deviceName ? `&name=${encodeURIComponent(deviceName)}` : "";
        // Re-dial the fresh session; the [sessionId,ticket] effect tears down + reconnects.
        navigate(`/viewer/${r.sessionId}?ticket=${encodeURIComponent(r.viewerTicket)}${nm}`);
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof ApiError ? err.status : 0;
        if (status === 401 || status === 403) {
          setReconnectFailed(true); // token no longer valid — can't auto-reconnect
          return;
        }
        if (performance.now() > deadline) {
          setReconnectFailed(true);
          return;
        }
        // 409 device_offline (still rebooting) / 429 / network -> keep waiting.
        const delay = Math.min(15000, 4000 * Math.pow(1.4, attempt - 1));
        timer = setTimeout(tick, delay);
      }
    };
    reconnectCancelRef.current = () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // reconnectNonce lets "Try again" re-run this poller even though `reconnecting`
    // stays true across a give-up; connectionLost gates the FIRST attempt to after the
    // host is genuinely down.
  }, [reconnecting, connectionLost, reconnectNonce, deviceName, navigate]);

  const cancelReconnect = useCallback(() => {
    reconnectCancelRef.current?.();
    rebootPendingRef.current = false;
    setReconnecting(false);
    setReconnectFailed(false);
  }, []);
  const retryReconnect = useCallback(() => {
    setReconnectFailed(false);
    setReconnectAttempt(0);
    setReconnecting(true);
    setReconnectNonce((n) => n + 1); // force the poller effect to re-run
  }, []);

  // A reboot is "safe" (won't strand the machine) only if the host auto-signs-in on boot
  // AND has a fixed unattended password (the per-launch password rotates on restart) AND
  // we hold a reconnect token.
  const rebootSafe = !!(hostCaps && hostCaps.autologon && hostCaps.fixedPw && hasReconnectToken);
  const doReboot = useCallback(async () => {
    rebootPendingRef.current = true; // mark the imminent drop as intentional
    setReconnectFailed(false);
    setReconnecting(true);
    setShowRebootModal(false);
    // Refresh the reconnect token so it's valid for the whole post-reboot reconnect
    // window even if this session has been open longer than the token's TTL.
    if (sessionId) {
      try {
        const r = await api<{ reconnectToken: string }>(`/v1/connect/reconnect-token`, {
          method: "POST",
          body: JSON.stringify({ sessionId })
        });
        reconnectTokenRef.current = r.reconnectToken;
        try {
          sessionStorage.setItem(RECONNECT_TOKEN_KEY, r.reconnectToken);
        } catch {
          /* ignore */
        }
        setHasReconnectToken(true);
      } catch {
        /* keep the existing token */
      }
    }
    ctlSend({ op: "reboot" });
  }, [ctlSend, sessionId]);
  const requestReboot = useCallback(() => {
    if (!hostCaps?.canReboot || !hasControl) return;
    if (!rebootSafe) {
      setShowRebootModal(true); // warn about stranding before allowing "Reboot anyway"
      return;
    }
    doReboot();
  }, [hostCaps, hasControl, rebootSafe, doReboot]);

  // ---- File channel: remote browse + download ----
  const fileSend = useCallback((obj: unknown) => {
    const ch = fileChannelRef.current;
    if (ch && ch.readyState === "open") ch.send(JSON.stringify(obj));
  }, []);
  const browse = useCallback((p: string) => fileSend({ op: "browse", path: p }), [fileSend]);
  const downloadRemote = useCallback((p: string) => fileSend({ op: "download", path: p }), [fileSend]);

  // Cancel an in-progress upload/download: stop the local loop, tell the host to
  // discard the partial file, and mark the row.
  const cancelTransfer = useCallback((id: string) => {
    abortedRef.current.add(id);
    const ch = fileChannelRef.current;
    if (ch && ch.readyState === "open") ch.send(JSON.stringify({ op: "cancel", id }));
    downloadsRef.current.delete(id);
    setFiles((prev) =>
      prev.map((f) =>
        f.id === id && (f.status === "offered" || f.status === "transferring")
          ? { ...f, status: "error", detail: "Cancelled" }
          : f
      )
    );
  }, []);

  // Send file(s) chosen via the picker button.
  const onPickFiles = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = Array.from(e.target.files ?? []);
      for (const f of list) uploadFile(f);
      e.target.value = ""; // let the same file be re-picked
    },
    [uploadFile]
  );

  // ---- Chat ----
  const sendChat = useCallback(() => {
    const text = chatInput.trim();
    const ch = chatChannelRef.current;
    if (!text || !ch || ch.readyState !== "open") return;
    ch.send(JSON.stringify({ op: "msg", text }));
    setChatMessages((p) => [...p, { from: "you", text }]);
    setChatInput("");
  }, [chatInput]);

  // ---- Session recording (records the incoming video stream to a .webm) ----
  // Best-effort server archive: presign a PUT, upload straight to object storage, then
  // finalize (writes the recordings row). Any failure (recordings disabled, offline
  // storage) is swallowed — the local .webm is already saved, so nothing is lost.
  const uploadRecording = useCallback(
    async (blob: Blob, durationSec: number) => {
      if (!sessionId) return;
      try {
        const meta = await api<{ recordingId: string; uploadUrl: string; storageKey: string }>(
          `/v1/sessions/${sessionId}/recording`,
          {
            method: "POST",
            body: JSON.stringify({ contentType: blob.type || "video/webm", bytes: blob.size })
          }
        );
        const put = await fetch(meta.uploadUrl, {
          method: "PUT",
          headers: { "content-type": blob.type || "video/webm" },
          body: blob
        });
        if (!put.ok) throw new Error(`upload ${put.status}`);
        await api(`/v1/sessions/${sessionId}/recording/${meta.recordingId}`, {
          method: "POST",
          body: JSON.stringify({ bytes: blob.size, durationSec })
        });
      } catch (err) {
        logger.warn("recording upload skipped (kept local copy)", { error: String(err) });
      }
    },
    [sessionId]
  );

  const toggleRecording = useCallback(async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    const stream = videoRef.current?.srcObject as MediaStream | null;
    if (!stream) return;
    // Descriptive filename: device label/hostname + ISO timestamp (safe chars only).
    const base = (deviceName || `session-${sessionId?.slice(0, 8) ?? "rec"}`).replace(/[^\w.-]+/g, "_");
    recNameRef.current = `${base}-${new Date().toISOString().replace(/[:.]/g, "-")}.webm`;
    // Pin a codec for consistent cross-browser playback.
    const codecs = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
    const mimeType =
      typeof MediaRecorder !== "undefined"
        ? codecs.find((t) => MediaRecorder.isTypeSupported(t)) ?? "video/webm"
        : "video/webm";
    recBytesRef.current = 0;
    recStartRef.current = performance.now();
    recordedChunksRef.current = [];
    setRecBytes(0);
    setRecElapsed(0);
    try {
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (ev) => {
        if (!ev.data || ev.data.size === 0) return;
        recordedChunksRef.current.push(ev.data);
        recBytesRef.current += ev.data.size;
        setRecBytes(recBytesRef.current);
        // Guardrail: stop before an unbounded in-memory buffer can crash the tab (~2 GB).
        if (recBytesRef.current > 2 * 1024 * 1024 * 1024) {
          try {
            mr.stop();
          } catch {
            /* ignore */
          }
        }
      };
      mr.onstop = () => {
        const durationSec = Math.max(0, Math.round((performance.now() - recStartRef.current) / 1000));
        const type = mr.mimeType || mimeType;
        const blob = new Blob(recordedChunksRef.current, { type });
        recordedChunksRef.current = [];
        saveBlob(blob, recNameRef.current); // always keep a local copy
        void uploadRecording(blob, durationSec); // best-effort server archive/index
        setRecording(false);
      };
      mr.start(1000);
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      logger.warn("recording failed", { error: String(err) });
    }
  }, [recording, sessionId, deviceName, uploadRecording]);

  // Live "REC mm:ss" tick while recording (size is updated per data chunk).
  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => {
      setRecElapsed(Math.max(0, Math.round((performance.now() - recStartRef.current) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [recording]);

  const getStatusColor = (s: string) => {
    if (s === "connected") return "text-green-400 bg-green-400/10";
    if (s === "connecting" || s === "new") return "text-blue-400 bg-blue-400/10";
    if (s === "disconnected" || s === "failed" || s === "closed") return "text-red-400 bg-red-400/10";
    return "text-yellow-400 bg-yellow-400/10";
  };

  const statusLabel = {
    "new": "Initializing",
    "connecting": "Connecting",
    "connected": "Connected",
    "disconnected": "Disconnected",
    "failed": "Failed",
    "closed": "Closed",
    "joining": "Joining session",
    "waiting for host": "Awaiting host",
    "received offer": "Establishing connection",
    "host disconnected": "Host disconnected"
  }[status] || status;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-950 to-slate-900">
      {/* Responsive top toolbar: primary controls inline, everything else in a
          "Tools" overflow menu (reachable at any width), plus a hide/reveal
          chevron so full-screen can use the whole viewport. */}
      {toolbarHidden ? (
        // Floated (fixed) so it overlays the video instead of reserving a row —
        // otherwise it leaves a black strip at the top in fullscreen and the
        // letterbox fit pushes the remote taskbar off the bottom.
        <button
          type="button"
          onClick={() => setToolbarHidden(false)}
          title="Show toolbar"
          className="fixed top-0 left-1/2 -translate-x-1/2 z-50 px-6 py-0.5 rounded-b-lg bg-slate-900/70 hover:bg-slate-900 border border-t-0 border-white/10 text-white/60 hover:text-white transition"
        >
          <Icon n="chevronDown" c="w-4 h-4" />
        </button>
      ) : (
        <div className="relative z-30 flex-shrink-0 bg-slate-900/95 border-b border-white/10 px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3">
          {/* Brand → back to the console */}
          <button
            type="button"
            onClick={() => navigate("/connect")}
            title="Back to console"
            className="shrink-0 flex items-center gap-2 hover:opacity-80 transition"
          >
            <img src="/logo.png" alt="SPIDER Viewer" className="w-6 h-6" />
            <span className="hidden lg:inline text-sm font-semibold text-white tracking-tight">
              SPIDER<span className="text-white/40">® Viewer</span>
            </span>
          </button>

          <div className="h-5 w-px bg-white/10 shrink-0" />

          {/* Status */}
          <div className={`shrink-0 flex items-center gap-2 px-2.5 py-1 rounded-lg ${getStatusColor(status)}`}>
            <div className={`w-2 h-2 rounded-full ${status === "connected" ? "bg-green-400 animate-pulse" : "bg-current"}`} />
            <span className="text-xs font-medium hidden sm:inline">{statusLabel}</span>
          </div>

          {/* Primary controls */}
          <label className="shrink-0 flex items-center gap-1.5 text-white/70 text-xs">
            <span className="hidden md:inline">Quality</span>
            <select
              value={quality}
              onChange={(e) => changeQuality(e.target.value)}
              disabled={!controlReady}
              className="bg-slate-800 border border-white/10 rounded px-2 py-1 text-white outline-none disabled:opacity-40"
            >
              <option value="sharp">Sharp text (recommended)</option>
              <option value="auto">Auto</option>
              <option value="smooth">Smooth (60fps)</option>
              <option value="balanced">Balanced</option>
            </select>
          </label>
          <label className="shrink-0 flex items-center gap-1.5 text-white/70 text-xs">
            <span className="hidden md:inline">View</span>
            <select
              value={displayMode}
              onChange={(e) => setDisplayMode(e.target.value as "fit" | "actual")}
              className="bg-slate-800 border border-white/10 rounded px-2 py-1 text-white outline-none"
            >
              <option value="fit">Fit</option>
              <option value="actual">Actual size</option>
            </select>
          </label>

          {/* v2 monitor picker: grouped displays + prev/next cycler. Shown once the host
              has advertised any capture source (degrades to a flat list for old hosts). */}
          {sources.length > 0 && (
            <div className="shrink-0 flex items-center gap-1 text-white/70 text-xs">
              <Icon n="monitor" c="w-4 h-4" />
              <button
                type="button"
                onClick={() => cycleMonitor(-1)}
                disabled={!controlReady || monitorSources.length < 2}
                title="Previous monitor"
                className="px-1.5 py-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ‹
              </button>
              <select
                value={currentSource}
                onChange={(e) => changeSource(e.target.value)}
                disabled={!controlReady}
                className="bg-slate-800 border border-white/10 rounded px-2 py-1 text-white outline-none disabled:opacity-40 max-w-[11rem]"
              >
                {(() => {
                  const scr = sources.filter((s) => s.kind === "screen");
                  const all = sources.filter((s) => s.kind === "all");
                  const win = sources.filter((s) => s.kind === "window");
                  const oth = sources.filter((s) => s.kind === "test");
                  const legacy = sources.filter((s) => !s.kind);
                  const opt = (s: RemoteSource) => (
                    <option key={s.id} value={s.id}>
                      {s.label || s.name}
                    </option>
                  );
                  return (
                    <>
                      {all.length > 0 && <optgroup label="Combined">{all.map(opt)}</optgroup>}
                      {scr.length > 0 && <optgroup label="Monitors">{scr.map(opt)}</optgroup>}
                      {win.length > 0 && <optgroup label="Windows">{win.map(opt)}</optgroup>}
                      {oth.length > 0 && <optgroup label="Other">{oth.map(opt)}</optgroup>}
                      {legacy.length > 0 && legacy.map(opt)}
                    </>
                  );
                })()}
              </select>
              <button
                type="button"
                onClick={() => cycleMonitor(1)}
                disabled={!controlReady || monitorSources.length < 2}
                title="Next monitor"
                className="px-1.5 py-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ›
              </button>
            </div>
          )}

          {/* Tools overflow — keyboard combos, blank, record, reboot */}
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setShowTools((v) => !v)}
              className={`px-2 py-1 rounded text-xs inline-flex items-center gap-1 ${showTools ? "bg-white/15 text-white" : "bg-white/5 hover:bg-white/10 text-white/80"}`}
            >
              <Icon n="more" c="w-4 h-4" />
              <span className="hidden sm:inline">Tools</span>
              <Icon n="chevronDown" c="w-3 h-3" />
            </button>
            {showTools && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowTools(false)} />
                <div className="absolute left-0 mt-2 z-50 w-64 rounded-xl bg-slate-800 border border-white/10 shadow-2xl p-3 space-y-3 text-xs">
                  {/* Monitor picker moved to the primary toolbar (grouped + prev/next). */}
                  <div>
                    <span className="text-white/40 uppercase tracking-wide mb-1.5 inline-flex items-center gap-1">
                      <Icon n="keyboard" c="w-3.5 h-3.5" /> Send keys
                    </span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {[
                        { k: "cad", label: "Ctrl+Alt+Del" },
                        { k: "taskmgr", label: "Task Mgr" },
                        { k: "win", label: "Win" },
                        { k: "alttab", label: "Alt+Tab" },
                        { k: "winr", label: "Win+R" },
                        { k: "wine", label: "Win+E" },
                        { k: "wind", label: "Win+D" },
                        { k: "prtsc", label: "PrtSc" }
                      ].map((b) => (
                        <button
                          key={b.k}
                          type="button"
                          onClick={() => sendKeycombo(b.k)}
                          disabled={!controlReady || hostAllowsInput === false}
                          title={hostAllowsInput === false ? "The remote hasn't enabled remote control" : ""}
                          className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/80 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          {b.label}
                        </button>
                      ))}
                    </div>
                    {/* Be honest about what will actually happen. Without the
                        host's elevated service, Ctrl+Alt+Del is only three
                        injected keys (Windows reserves the real sequence for
                        LocalSystem services) and nothing reaches a UAC prompt or
                        an administrator window. */}
                    {hostCaps && !hostCaps.elevated && (
                      <p className="mt-2 text-[11px] leading-snug text-amber-200/80">
                        Administrator control is off on the remote PC: UAC prompts and apps run as
                        administrator won&apos;t respond, and Ctrl+Alt+Del is best-effort. Turn on
                        &ldquo;Allow control of administrator prompts&rdquo; in SPIDER Viewer there.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1.5 pt-2 border-t border-white/10">
                    <button
                      type="button"
                      onClick={toggleBlank}
                      disabled={!controlReady}
                      className={`flex-1 px-2 py-1.5 rounded inline-flex items-center justify-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed ${blankOn ? "bg-purple-500/30 text-purple-200" : "bg-white/5 hover:bg-white/10 text-white/80"}`}
                    >
                      <Icon n={blankOn ? "eyeOff" : "eye"} c="w-3.5 h-3.5" />
                      {blankOn ? "Unblank" : "Blank"}
                    </button>
                    <button
                      type="button"
                      onClick={toggleRecording}
                      disabled={!hasVideo}
                      title={hasVideo ? "" : "Waiting for video to start"}
                      className={`flex-1 px-2 py-1.5 rounded inline-flex items-center justify-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed ${recording ? "bg-red-500/30 text-red-200" : "bg-white/5 hover:bg-white/10 text-white/80"}`}
                    >
                      <Icon n={recording ? "stop" : "record"} c="w-3.5 h-3.5" />
                      {recording ? `Stop · ${fmtDuration(recElapsed)} · ${fmtBytes(recBytes)}` : "Record"}
                    </button>
                  </div>
                  {/* v2 remote reboot — only for a v2 host; the button gates on control,
                      and requestReboot pre-flights the strand risk before acting. */}
                  {hostCaps && (
                    <div className="pt-2 border-t border-white/10">
                      <button
                        type="button"
                        onClick={requestReboot}
                        disabled={!hasControl || !hostCaps.canReboot}
                        title={
                          !hostCaps.canReboot
                            ? "This host doesn't support remote reboot"
                            : !hasControl
                              ? "The remote hasn't enabled remote control"
                              : "Restart the remote machine"
                        }
                        className="w-full px-2 py-1.5 rounded inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-red-500/20 text-white/80 hover:text-red-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Icon n="power" c="w-3.5 h-3.5" /> Reboot host
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right cluster: stats (wide only) + panel/fullscreen/hide */}
          <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="hidden xl:flex items-center gap-3 text-xs text-white/70">
              {stats.latency !== undefined && (
                <span>Latency <b className="font-mono font-medium text-white">{stats.latency}ms</b></span>
              )}
              {stats.bandwidth !== undefined && (
                <span><b className="font-mono font-medium text-white">{stats.bandwidth}</b> Mbps</span>
              )}
              {stats.fps !== undefined && (
                <span><b className="font-mono font-medium text-white">{stats.fps}</b> FPS</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowRightPanel(!showRightPanel)}
              className="p-2 hover:bg-white/10 rounded-lg transition text-white/70 hover:text-white"
              title={showRightPanel ? "Hide side panel" : "Show side panel"}
            >
              <Icon n="panel" />
            </button>
            <button
              type="button"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 hover:bg-white/10 rounded-lg transition text-white/70 hover:text-white"
              title={isFullscreen ? "Exit full screen" : "Full screen"}
            >
              <Icon n={isFullscreen ? "minimize" : "maximize"} />
            </button>
            <button
              type="button"
              onClick={() => setToolbarHidden(true)}
              className="p-2 hover:bg-white/10 rounded-lg transition text-white/50 hover:text-white"
              title="Hide toolbar"
            >
              <Icon n="chevronUp" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content — in fullscreen, drop all padding/gap so the remote screen
          fills the viewport edge-to-edge (no rounded corners, no letterbox gap). */}
      <div className={`flex-1 flex overflow-hidden ${isFullscreen ? "gap-0 p-0" : "gap-4 p-4"}`}>
        {/* Video Area */}
        <div className="flex-1 flex flex-col">
          <div
            className={`relative flex-1 flex items-center justify-center bg-black ${isFullscreen ? "" : "rounded-xl"} ${displayMode === "actual" ? "overflow-auto" : "overflow-hidden"} ${dragOver ? "ring-2 ring-accent" : ""} transition`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            {/* While controlling, show a real-time local CROSSHAIR rather than hiding
                the cursor (cursor-none). The host bakes its own arrow into the video
                stream, but that only updates at the stream frame rate — at low/stalled
                fps it lags or freezes, so hiding the local cursor left the operator with
                no pointer at all ("cursor disappeared"). The crosshair is drawn locally
                at 60fps, is visually distinct from the remote arrow, and its centre
                hotspot sits exactly on the injected click point (absolute-coordinate
                injection), so it reads as the authoritative aim point. */}
            <video
              ref={videoRef}
              className={`bg-black ${
                displayMode === "fit" || !hasVideo
                  ? "w-full h-full object-contain"
                  : "max-w-none max-h-none object-none m-auto"
              } ${hasControl ? "cursor-crosshair" : "cursor-default"}`}
              autoPlay
              playsInline
              muted
            />

            {/* v2 remote-cursor overlay (view-only): a single smooth interpolated pointer
                driven by the rAF loop above, hidden while controlling (native crosshair
                wins) and when no fresh sample. pointer-events-none so input flows under it. */}
            <div
              ref={cursorOverlayRef}
              className="absolute top-0 left-0 pointer-events-none z-20"
              style={{ display: "none", willChange: "transform" }}
              aria-hidden="true"
            />

            {/* v2 recording badge. */}
            {recording && (
              <div className="absolute top-4 left-4 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/80 text-white flex items-center gap-1.5 pointer-events-none z-20">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                REC {fmtDuration(recElapsed)} · {fmtBytes(recBytes)}
              </div>
            )}

            {/* SECURE DESKTOP. Windows draws UAC consent, the lock screen and the
                Ctrl+Alt+Del screen on a protected desktop that screen capture
                cannot record, so the video below is frozen on its last ordinary
                frame. These stills come from the host's elevated service. The
                layer is pointer-events-none on purpose: clicks fall through to
                the video's handlers, which map them against THIS image. */}
            {secureDesktop && secureFrame && !secureBlank && (
              <img
                ref={secureImgRef}
                src={secureFrame}
                alt="Remote secure desktop"
                className="absolute inset-0 w-full h-full object-contain bg-black pointer-events-none z-20"
              />
            )}
            {secureDesktop && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-500/25 text-blue-50 border border-blue-400/30 flex items-center gap-2 pointer-events-none z-30">
                <Icon n="lock" c="w-4 h-4" />
                {secureBlank
                  ? "Windows secure screen — can't be shown from this PC, but your typing still reaches it"
                  : secureFrame
                    ? "Windows secure screen — you can click and type here"
                    : "Windows secure screen — waiting for the remote…"}
              </div>
            )}

            {/* Control is automatic (RDP-style) — no "Take Control" button. This
                passive badge appears only when the host hasn't enabled remote input,
                to explain why the keyboard/mouse aren't driving the machine. */}
            {hostAllowsInput === false && (
              <div className="absolute top-4 right-4 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-500/20 text-amber-100 border border-amber-400/30 flex items-center gap-2 pointer-events-none">
                <Icon n="cursor" c="w-4 h-4" />
                View only — remote hasn&apos;t allowed control
              </div>
            )}

            {dragOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <div className="text-center">
                  <svg className="w-12 h-12 mx-auto mb-2 text-blue-400 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                  </svg>
                  <p className="text-white font-medium">Drop files to send</p>
                  <p className="text-white/50 text-xs mt-1">Accepted: documents, images, archives</p>
                </div>
              </div>
            )}

            {/* Black-screen explainer: the remote is locked, or no frames are
                arriving (display asleep / RDP-detached console / capture stall).
                pointer-events-none so control input still flows underneath. */}
            {/* ...unless the secure desktop is up: the video is *supposed* to be
                frozen then, and the stills above are the live picture. Dimming
                them behind "no video" would hide the very prompt being answered. */}
            {(hostLocked || noVideo) && !secureDesktop && !connectionLost && !dragOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none">
                <div className="text-center max-w-md px-6">
                  <svg className="w-10 h-10 mx-auto mb-3 text-amber-400/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {hostLocked ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                  <p className="text-white font-medium">
                    {hostLocked ? "Remote screen is locked" : "No video from the remote machine"}
                  </p>
                  <p className="text-white/60 text-xs mt-2 leading-relaxed">
                    {hostLocked
                      ? "The remote machine is at the Windows lock screen, which cannot be captured. Unlock the remote machine to restore the picture."
                      : "Its display may be asleep or its console detached (e.g. after an RDP session). The remote is retrying capture automatically — if this persists, open Tools on the remote's SPIDER Viewer console and install the RDP fix."}
                  </p>
                </div>
              </div>
            )}

            {/* v2 reboot auto-reconnect: while re-dialing a rebooted host (or after the
                poller gave up), show this instead of the terminal disconnect card. */}
            {(reconnecting || reconnectFailed) && !dragOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/85">
                <div className="text-center max-w-md px-6">
                  {!reconnectFailed ? (
                    <>
                      <div className="w-10 h-10 mx-auto mb-3 border-2 border-white/20 border-t-accent rounded-full animate-spin" />
                      <p className="text-white font-medium">Rebooting the remote machine…</p>
                      <p className="text-white/60 text-xs mt-2 leading-relaxed">
                        Waiting for it to come back online, then reconnecting automatically
                        {reconnectAttempt > 0 ? ` (attempt ${reconnectAttempt})` : ""}.
                      </p>
                      <button
                        type="button"
                        onClick={cancelReconnect}
                        className="mt-4 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <svg className="w-10 h-10 mx-auto mb-3 text-red-400/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-white font-medium">Couldn&apos;t reconnect automatically</p>
                      <p className="text-white/60 text-xs mt-2 leading-relaxed">
                        The remote machine didn&apos;t come back online in time, or the reconnect
                        session expired. You can retry or return to the console.
                      </p>
                      <div className="mt-4 flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={retryReconnect}
                          className="px-4 py-2 rounded-lg bg-accent hover:bg-accent/90 text-white text-sm font-medium transition"
                        >
                          Try again
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            cancelReconnect();
                            navigate("/connect");
                          }}
                          className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition"
                        >
                          Close
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Terminal disconnect: the remote peer is gone (it restarted or went
                offline). The stale frame is already cleared; show a clean card with
                a way out instead of a frozen desktop under a "retrying" hint. */}
            {connectionLost && !reconnecting && !reconnectFailed && !dragOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/85">
                <div className="text-center max-w-md px-6">
                  <svg className="w-10 h-10 mx-auto mb-3 text-red-400/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824-2.167a5 5 0 00-7.072 0m0 0L3 3" />
                  </svg>
                  <p className="text-white font-medium">Remote machine disconnected</p>
                  <p className="text-white/60 text-xs mt-2 leading-relaxed">
                    The connection was lost — the remote machine may be restarting or has gone offline.
                    Reconnect from the console once it&apos;s back online.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        window.close();
                      } catch {
                        /* not script-opened — fall through to navigation */
                      }
                      navigate("/connect");
                    }}
                    className="mt-4 px-4 py-2 rounded-lg bg-accent hover:bg-accent/90 text-white text-sm font-medium transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* v2 reboot pre-flight: warn when the host lacks auto sign-in + a fixed
                unattended password (or we hold no reconnect token), so a reboot could
                strand it offline. "Reboot anyway" is available for attended machines. */}
            {showRebootModal && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 p-6">
                <div className="max-w-sm w-full rounded-xl bg-slate-800 border border-white/10 p-5 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon n="power" c="w-5 h-5 text-amber-300" />
                    <h3 className="text-sm font-semibold text-white">Reboot may strand this machine</h3>
                  </div>
                  <p className="text-white/60 text-xs leading-relaxed">
                    Auto sign-in on boot and a fixed unattended password aren&apos;t both
                    configured on the remote, so after it restarts it may not come back
                    online for an unattended reconnect. If someone is present to sign in,
                    you can proceed.
                  </p>
                  {hostCaps && (
                    <ul className="mt-3 text-xs space-y-1">
                      <li className={hostCaps.autologon ? "text-green-300" : "text-amber-300"}>
                        {hostCaps.autologon ? "✓" : "✕"} Auto sign-in on boot
                      </li>
                      <li className={hostCaps.fixedPw ? "text-green-300" : "text-amber-300"}>
                        {hostCaps.fixedPw ? "✓" : "✕"} Fixed unattended password
                      </li>
                      <li className={hasReconnectToken ? "text-green-300" : "text-amber-300"}>
                        {hasReconnectToken ? "✓" : "✕"} Auto-reconnect token ready
                      </li>
                    </ul>
                  )}
                  <div className="mt-4 flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setShowRebootModal(false)}
                      className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={doReboot}
                      className="px-3 py-1.5 rounded-lg bg-red-500/80 hover:bg-red-500 text-white text-sm font-medium transition"
                    >
                      Reboot anyway
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar — hidden in fullscreen so the remote screen fills the display. */}
        {showRightPanel && !isFullscreen && (
          <div className="w-72 flex flex-col gap-4 overflow-y-auto">
            {/* Clipboard Panel */}
            <div className="glass p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Icon n="clipboard" c="w-4 h-4 text-white/50" />
                <h3 className="text-sm font-semibold text-white">Clipboard</h3>
              </div>
              <textarea
                readOnly
                value={remoteClipboard}
                placeholder="Remote clipboard contents will appear here…"
                className="min-h-16 bg-white/5 border border-white/10 rounded-lg p-2.5 text-xs font-mono resize-none outline-none focus:border-white/20 transition"
              />
              {remoteImage && (
                <div className="mt-2 rounded-lg border border-white/10 overflow-hidden bg-black/30">
                  <img src={remoteImage} alt="Remote clipboard" className="max-h-32 w-full object-contain" />
                  <button
                    type="button"
                    onClick={copyRemoteImage}
                    className="w-full text-xs px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 transition font-medium border-t border-white/10"
                  >
                    Copy image
                  </button>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={copyRemoteClipboard}
                  disabled={!remoteClipboard}
                  className="flex-1 text-xs px-3 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 disabled:opacity-30 disabled:cursor-not-allowed transition font-medium"
                >
                  Copy text
                </button>
                <button
                  type="button"
                  onClick={sendClipboard}
                  disabled={!clipReady}
                  title="Send this machine's clipboard (image or text) to the remote"
                  className="flex-1 text-xs px-3 py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-300 disabled:opacity-30 disabled:cursor-not-allowed transition font-medium"
                >
                  Send
                </button>
              </div>
            </div>

            {/* Files Panel — send up + get down, in one place */}
            <div className="glass p-4 flex flex-col flex-1 min-h-0">
              <div className="flex items-center gap-2 mb-3">
                <Icon n="swap" c="w-4 h-4 text-white/50" />
                <h3 className="text-sm font-semibold text-white">Files</h3>
              </div>

              {/* Send to remote — always available; the remote confirms each
                  incoming file with a Save dialog, so no global toggle is needed. */}
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onPickFiles} />
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`rounded-lg border border-dashed p-3 text-center transition ${
                  dragOver ? "border-blue-400 bg-blue-400/10" : "border-white/15"
                }`}
              >
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!fileReady}
                  className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 font-medium disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Icon n="upload" c="w-4 h-4" /> Choose files to send
                </button>
                <p className="text-[11px] text-white/40 mt-1.5">or drop files here / onto the screen</p>
              </div>

              {/* Active transfers */}
              {files.length > 0 && (
                <div className="mt-3 space-y-2 overflow-y-auto">
                  {files.map((f) => {
                    const pct = f.size > 0 ? Math.round((f.sent / f.size) * 100) : 100;
                    const active = f.status === "offered" || f.status === "transferring";
                    return (
                      <div key={f.id} className="bg-white/5 border border-white/10 rounded-lg p-2.5">
                        <div className="flex items-center gap-2">
                          <Icon
                            n={f.dir === "up" ? "upload" : "download"}
                            c={`w-3.5 h-3.5 flex-shrink-0 ${f.dir === "up" ? "text-blue-300" : "text-green-300"}`}
                          />
                          <span className="text-xs font-medium text-white truncate flex-1">{f.name}</span>
                          {f.status === "done" && <Icon n="check" c="w-3.5 h-3.5 text-green-400 flex-shrink-0" />}
                          {(f.status === "error" || f.status === "rejected") && (
                            <Icon n="x" c="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                          )}
                          {active && (
                            <button
                              type="button"
                              onClick={() => cancelTransfer(f.id)}
                              title="Cancel"
                              className="text-white/40 hover:text-red-300 flex-shrink-0"
                            >
                              <Icon n="x" c="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="mt-1 flex items-center justify-between text-[11px] text-white/50">
                          <span className="truncate">
                            {f.status === "transferring" && `${pct}% · ${f.dir === "up" ? "sending" : "receiving"}`}
                            {f.status === "offered" && "Waiting for host…"}
                            {f.status === "done" && "Complete"}
                            {f.status === "error" && (f.detail || "Error")}
                            {f.status === "rejected" && (f.detail || "Rejected")}
                          </span>
                          <span className="flex-shrink-0 ml-2">{formatBytes(f.size)}</span>
                        </div>
                        {active && (
                          <div className="w-full bg-white/10 rounded-full h-1.5 mt-1.5 overflow-hidden">
                            <div
                              className={`h-full transition-all ${f.dir === "up" ? "bg-blue-400" : "bg-green-400"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Get from remote (browser) */}
              <div className="mt-3 pt-3 border-t border-white/10 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-white/40 font-semibold">
                    <Icon n="download" c="w-3.5 h-3.5" /> Get from remote
                  </span>
                  <button
                    type="button"
                    onClick={() => browse("")}
                    disabled={!fileReady || hostAllowsFiles === false}
                    className="text-[11px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/70 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {browser ? "Home" : "Browse"}
                  </button>
                </div>
                {browseError && <p className="text-[11px] text-red-300 mb-1.5">{browseError}</p>}
                {browser ? (
                  <>
                    <p className="text-[10px] text-white/40 font-mono truncate mb-1" title={browser.path}>
                      {browser.path}
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-0.5">
                      <button
                        type="button"
                        onClick={() => browse(browser.parent)}
                        className="w-full text-left text-xs px-2 py-1 rounded hover:bg-white/10 text-white/60 flex items-center gap-2"
                      >
                        <Icon n="arrowUp" c="w-3.5 h-3.5" /> ..
                      </button>
                      {browser.entries.map((ent) => (
                        <button
                          key={ent.name}
                          type="button"
                          onClick={() =>
                            ent.isDir
                              ? browse(joinPath(browser.path, browser.sep, ent.name))
                              : downloadRemote(joinPath(browser.path, browser.sep, ent.name))
                          }
                          className="w-full text-left text-xs px-2 py-1 rounded hover:bg-white/10 flex items-center gap-2"
                        >
                          <Icon
                            n={ent.isDir ? "folder" : "file"}
                            c="w-3.5 h-3.5 text-white/50 flex-shrink-0"
                          />
                          <span className="truncate flex-1">{ent.name}</span>
                          {!ent.isDir && <Icon n="download" c="w-3.5 h-3.5 text-white/30 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </>
                ) : hostAllowsFiles === false ? (
                  <p className="text-[11px] text-amber-200/80">
                    Browsing is <b>off</b> on the remote. Ask them to enable “Allow remote file
                    access”. (Sending files to them still works.)
                  </p>
                ) : (
                  <p className="text-[11px] text-white/30">
                    Browse the remote machine's files and click a file to download it.
                  </p>
                )}
              </div>
            </div>

            {/* Chat Panel */}
            <div className="glass p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Icon n="chat" c="w-4 h-4 text-white/50" />
                <h3 className="text-sm font-semibold text-white">Chat</h3>
              </div>
              <div className="flex-1 min-h-20 max-h-40 overflow-y-auto space-y-1 mb-3">
                {chatMessages.length === 0 ? (
                  <p className="text-xs text-white/30">No messages yet</p>
                ) : (
                  chatMessages.map((m, i) => (
                    <div
                      key={i}
                      className={`text-xs ${m.from === "you" ? "text-blue-300" : "text-white/80"}`}
                    >
                      <span className="font-medium">{m.from === "you" ? "You" : "Remote"}:</span>{" "}
                      {m.text}
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendChat();
                  }}
                  placeholder="Message…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg p-2 text-xs outline-none focus:border-white/20"
                />
                <button
                  type="button"
                  onClick={sendChat}
                  disabled={!chatReady}
                  className="text-xs px-3 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 font-medium inline-flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Icon n="send" c="w-3.5 h-3.5" /> Send
                </button>
              </div>
            </div>

            {/* Connection Info */}
            <div className="glass p-3 text-xs space-y-2">
              <div className="flex justify-between">
                <span className="text-white/50">Session ID:</span>
                <span className="font-mono text-white/70">{sessionId?.slice(0, 8)}...</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Control:</span>
                <span className={hasControl ? "text-green-400 font-medium" : "text-white/50"}>{hasControl ? "Active" : "Inactive"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Data Channel:</span>
                <span className={channelReady ? "text-green-400 font-medium" : "text-white/50"}>{channelReady ? "Ready" : "Connecting"}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

function joinPath(base: string, sep: string, name: string): string {
  return base.endsWith(sep) ? base + name : base + sep + name;
}

// v2 recording readout formatters.
function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
function fmtBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(0, Math.round(n / 1024))} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// v2 remote-cursor overlay glyphs. White-filled, black-stroked inline SVGs so the
// cursor stays visible on any background; the hotspot (the actual click point) is
// subtracted in the rAF loop so the glyph lines up with the real remote pointer.
const CURSOR_HOTSPOT: Record<string, [number, number]> = {
  default: [1, 1],
  text: [4, 9],
  hand: [8, 2],
  busy: [9, 9],
  progress: [9, 9],
  crosshair: [9, 9],
  move: [9, 9],
  "ew-resize": [9, 9],
  "ns-resize": [9, 9],
  "nwse-resize": [9, 9],
  "nesw-resize": [9, 9],
  "not-allowed": [9, 9]
};
function cursorGlyph(shape: string): string {
  const arrow = `<svg width="20" height="20" viewBox="0 0 20 20"><path d="M2 1l0 14 3.5-3.5 2.2 5 2.4-1-2.2-5 5 0z" fill="#fff" stroke="#000" stroke-width="1.2" stroke-linejoin="round"/></svg>`;
  const ibeam = `<svg width="8" height="18" viewBox="0 0 8 18"><path d="M2 1h4M2 17h4M4 1v16" stroke="#000" stroke-width="1.4" fill="none"/><path d="M3 1h2M3 17h2M4 1v16" stroke="#fff" stroke-width="0.6" fill="none"/></svg>`;
  const hand = `<svg width="18" height="20" viewBox="0 0 18 20"><path d="M6 8V3a1.5 1.5 0 013 0v4a1.5 1.5 0 013 0 1.5 1.5 0 013 0v6a5 5 0 01-5 5H9l-4-4-2-3a1.5 1.5 0 012-2l1 1z" fill="#fff" stroke="#000" stroke-width="1.1"/></svg>`;
  const spin = `<svg width="18" height="18" viewBox="0 0 18 18"><g fill="none" stroke-width="2"><circle cx="9" cy="9" r="6" stroke="#000" opacity="0.25"/><path d="M9 3a6 6 0 016 6" stroke="#fff"><animateTransform attributeName="transform" type="rotate" from="0 9 9" to="360 9 9" dur="0.8s" repeatCount="indefinite"/></path></g></svg>`;
  const resize = (rot: number) =>
    `<svg width="18" height="18" viewBox="0 0 18 18"><g transform="rotate(${rot} 9 9)"><path d="M2 9h14M2 9l3-3M2 9l3 3M16 9l-3-3M16 9l-3 3" stroke="#000" stroke-width="2" fill="none"/><path d="M2 9h14M2 9l3-3M2 9l3 3M16 9l-3-3M16 9l-3 3" stroke="#fff" stroke-width="0.8" fill="none"/></g></svg>`;
  switch (shape) {
    case "text":
      return ibeam;
    case "hand":
      return hand;
    case "busy":
    case "progress":
      return spin;
    case "ew-resize":
      return resize(0);
    case "ns-resize":
      return resize(90);
    case "nwse-resize":
      return resize(45);
    case "nesw-resize":
      return resize(-45);
    case "move":
      return resize(0);
    default:
      return arrow;
  }
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  // Encode in chunks to avoid String.fromCharCode argument-count limits.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + CHUNK) as unknown as number[]
    );
  }
  return btoa(binary);
}
